/* The admin console uses the full screen width (desktop-first, still usable on mobile). */
.page {
  position: fixed;
  inset: 0;
  z-index: 60;
  overflow-y: auto;
  background: var(--bg);
  display: grid;
  grid-template-columns: 220px 1fr;
  grid-template-rows: auto 1fr;
}

.header {
  grid-column: 1 / -1;
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: calc(var(--safe-t) + 8px) 16px 8px 8px;
  background: rgb(0 0 0 / 0.9);
  backdrop-filter: blur(16px);
  border-bottom: 1px solid var(--border);
}

.title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.badge {
  font-size: 11px;
  font-weight: 800;
  padding: 3px 8px;
  border-radius: 6px;
  background: var(--accent-soft);
  color: var(--accent);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.role {
  font-size: var(--fs-xs);
  color: var(--text-3);
  font-weight: 700;
}

.nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 16px 10px;
  border-right: 1px solid var(--border);
  position: sticky;
  top: 60px;
  align-self: start;
}

.navItem {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: var(--r-sm);
  font-weight: 600;
  font-size: var(--fs-sm);
  color: var(--text-2);
  text-align: left;
}

.navItem:hover {
  background: var(--surface-1);
  color: var(--text);
}

.navActive {
  background: var(--surface-2);
  color: var(--text);
}

.navActive svg {
  color: var(--accent);
}

.main {
  padding: 20px 24px 60px;
  min-width: 0;
}

@media (max-width: 760px) {
  .page {
    grid-template-columns: 1fr;
  }
  .nav {
    flex-direction: row;
    overflow-x: auto;
    border-right: 0;
    border-bottom: 1px solid var(--border);
    padding: 8px;
    position: static;
  }
  .navItem {
    white-space: nowrap;
  }
  .main {
    padding: 16px;
  }
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.toolbar h2,
.h2 {
  font-size: var(--fs-lg);
  font-weight: 800;
}

.h2 {
  margin: 28px 0 12px;
}

.toolbar > div[role='tablist'] {
  padding: 0;
}

.kpis {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
}

.kpi {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 16px;
  border-radius: var(--r-md);
  background: var(--surface-1);
  border: 1px solid var(--border);
}

.kpi span {
  font-size: var(--fs-xs);
  color: var(--text-3);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.kpi strong {
  font-size: var(--fs-xl);
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.kpi small {
  font-size: var(--fs-xs);
  color: var(--accent);
  font-weight: 700;
}

.chart {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 180px;
  padding: 12px;
  border-radius: var(--r-md);
  background: var(--surface-1);
  border: 1px solid var(--border);
}

.bar {
  position: relative;
  flex: 1;
  height: 100%;
  display: flex;
  align-items: flex-end;
  gap: 1px;
}

.bar small {
  position: absolute;
  bottom: -18px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  color: var(--text-3);
}

.barViews,
.barPlays {
  display: inline-block;
  flex: 1;
  min-height: 2px;
  border-radius: 3px 3px 0 0;
}

.barViews {
  background: var(--accent);
}

.barPlays {
  background: #8f7bff;
}

.legend {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 26px;
  font-size: var(--fs-xs);
  color: var(--text-3);
}

.legend i {
  width: 10px;
  height: 10px;
  flex: none;
  border-radius: 3px;
}

.muted {
  color: var(--text-3);
  font-size: var(--fs-sm);
  margin-bottom: 12px;
}

.cards {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 10px;
}

.card {
  padding: 14px;
  border-radius: var(--r-md);
  background: var(--surface-1);
  border: 1px solid var(--border);
}

.cardTop {
  display: flex;
  gap: 12px;
}

.thumb {
  width: 54px;
  height: 72px;
  object-fit: cover;
  border-radius: 8px;
  flex-shrink: 0;
}

.cardText {
  min-width: 0;
}

.target {
  margin: 6px 0 4px;
  font-weight: 700;
}

.cardActions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  justify-content: flex-end;
}

.pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: var(--r-full);
  background: var(--surface-3);
  font-size: 11px;
  font-weight: 700;
  color: var(--text-2);
}

.pillWarn {
  background: rgb(255 181 71 / 0.15);
  color: var(--warning);
}

.pillDanger {
  background: rgb(255 77 94 / 0.15);
  color: var(--danger);
}

.search {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 12px;
  border-radius: var(--r-sm);
  background: var(--surface-2);
  color: var(--text-3);
  min-width: 240px;
}

.search input {
  flex: 1;
  background: none;
  border: 0;
  outline: none;
  color: var(--text);
}

.tableWrap {
  overflow-x: auto;
  border-radius: var(--r-md);
  border: 1px solid var(--border);
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--fs-sm);
}

.table th {
  text-align: left;
  font-size: var(--fs-xs);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-3);
  padding: 10px 12px;
  background: var(--surface-1);
  white-space: nowrap;
}

.table td {
  padding: 10px 12px;
  border-top: 1px solid var(--border);
  white-space: nowrap;
}

.userCell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.userCell span {
  display: flex;
  flex-direction: column;
}

.userCell small {
  color: var(--text-3);
  font-size: var(--fs-xs);
}

.miniCover {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  object-fit: cover;
  background: var(--surface-3);
  display: inline-block;
}

.select {
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 6px 8px;
}

.table input[type='checkbox'] {
  accent-color: var(--accent);
  width: 18px;
  height: 18px;
}

.videoGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
}

.videoCard {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.videoThumb {
  position: relative;
  aspect-ratio: 9 / 16;
  border-radius: var(--r-sm);
  overflow: hidden;
  background: var(--surface-2);
}

.videoThumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.videoStatus {
  position: absolute;
  top: 6px;
  left: 6px;
}

.videoCaption {
  font-size: var(--fs-xs);
  color: var(--text-2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.videoActions {
  display: flex;
  gap: 6px;
}

.audit {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 6px;
}

.audit li {
  display: grid;
  grid-template-columns: 180px 1fr auto;
  gap: 4px 12px;
  align-items: baseline;
  padding: 10px 12px;
  border-radius: var(--r-sm);
  background: var(--surface-1);
  font-size: var(--fs-sm);
}

.audit code {
  color: var(--accent);
  font-weight: 700;
}

.audit li > small {
  grid-column: 2 / 3;
  margin: 0;
  word-break: break-all;
}

.audit time {
  color: var(--text-3);
  font-size: var(--fs-xs);
}
